// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import MiniMyAccount from "@west/App/Components/MiniMyAccount/MiniMyAccount";
import HeaderSignInNavigationDrawer from "@west/App/Components/NavigationDrawer/HeaderSignInNavigationDrawer";
import NeedHelpNavigationDrawer from "@west/App/Components/NavigationDrawer/NeedHelpNavigationDrawer";
import { CustomAppState } from "@west/App/Store/Reducers";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import { NeedHelpComponent } from "@west/App/Widgets/Header/NeedHelp";
import { ZipCode } from "@west/App/Widgets/Header/ZipCode";
import { RowWrapper } from "@west/MobiusOverrides/PanelMenu";
import Close from "@west/XDL/Icons/Close";
import Menu from "@west/XDL/Icons/Menu";
import { color, hideElementOnPrint, xScreen } from "@west/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        // Xng-Changes
        isAuthenticated: xIsAuthenticated(state),
        headerHeight: state.xHeader.headerHeight,
        hasMiniCart: getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Common/CartLink";
        })?.fields,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        settings: getSettingsCollection(state),
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => {
        isLoaded: boolean;
        loadError: Error | undefined;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    mainNavigationRowTypography: {
        transform: "capitalize",
        color: "text",
        weight: "normal",
        size: 14,
    },
    drawerSectionWrapper: {
        css: css`
            &.separate {
                padding-top: 16px;
                margin-top: 16px;
                border-top: 1px solid ${color("common.border")};
            }

            &.tablet {
                display: none;

                ${xScreen("lte", "md")} {
                    display: block;
                }
            }
            &.mobile-only {
                display: none;

                ${xScreen("lt", "sm")} {
                    display: block;
                }
            }
        `,
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = (props: Props) => {
    const {
        links,
        showQuickOrder,
        quickOrderLink,
        displayVmiNavigation,
        vmiPageLinks,
        isCompactHeaderFlyout,
        isPunchOutSession,
        showFlyoutPricingToggle,
        showFlyoutInventoryToggle,
        currentLocation,
        headerLinkListLinkFields,
        navDrawerIsOpen,
        flyoutDrawerIsOpen,
        history,
        setInitialValues,
        setNavDrawerIsOpen,
        setFlyoutDrawerIsOpen,
        signOut,
        cancelPunchOut,
        headerHeight,
        hasMiniCart,
        cartPageUrl,
        isAuthenticated,
        settings,
        useLoadScript,
    } = props;
    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        closeDrawer();
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    return (
        <>
            <Button
                className="menu-trigger-button"
                onClick={isOpen ? closeDrawer : openDrawer}
                {...styles.menuTriggerButton}
                aria-label={translate("Expand Menu")}
                data-test-selector="expandMobileMenu"
            >
                <ButtonIcon className="menu-trigger-button-icon" src={isOpen ? Close : Menu} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                className="drawer"
                draggable={false}
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                cssOverrides={{
                    scrim: css`
                        top: ${headerHeight};
                        ${hideElementOnPrint}
                    `,
                    drawerTitle: css`
                        display: none;
                    `,
                    drawerContent: css`
                        margin-top: -2px;
                        overflow-x: hidden;
                    `,
                    drawerBody: css`
                        ${isOpen &&
                        css`
                            ${xScreen("lte", "xs")} {
                                max-width: 100vw;
                                width: 100%;
                            }
                        `}
                        top: ${headerHeight};
                        padding-top: 12px;
                        background: ${color("common.white")};
                        box-shadow: none;
                        border-top: 1px solid ${color("common.border")};
                    `,
                }}
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            if (link.children && link.children.length > 0) {
                                let menu;
                                let panelRow;
                                const renderChildren = link.children && link.children.length > 0;

                                let menuItem: JSX.Element | null = (
                                    <PanelRow
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                        className="main-navigation-row"
                                        onClick={closeDrawer}
                                        isCurrent={currentPageUrl === link.url}
                                        {...styles.mainNavigationRow}
                                        color="common.ui8"
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                );

                                if (renderChildren) {
                                    if (link.url) {
                                        panelRow = (
                                            <PanelRow isCurrent={currentPageUrl === link.url} hasChildren></PanelRow>
                                        );
                                    } else {
                                        panelRow = React.cloneElement(menuItem, { hasChildren: true });
                                        menuItem = null;
                                    }
                                    menu = (
                                        <PanelMenu
                                            className="panel-menu"
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`PanelMenu_${index}`}
                                            currentUrl={currentPageUrl}
                                            closeOverlay={closeDrawer}
                                            panelTrigger={panelRow}
                                            menuItems={link.children}
                                            maxDepth={link.maxDepth || 3}
                                            layer={0}
                                            {...styles.panelMenu}
                                        />
                                    );
                                }

                                return (
                                    <RowWrapper
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`RowWrapper_${index}`}
                                        isCurrent={currentPageUrl === link.url}
                                    >
                                        {menuItem}
                                        {menu}
                                    </RowWrapper>
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}
                    {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                        <PanelRow
                            className="main-navigation-row"
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {quickOrderLink.title}
                            </Typography>
                        </PanelRow>
                    )}
                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>
                <StyledSection className="drawer-section-wrapper separate" {...styles.drawerSectionWrapper}>
                    <NeedHelpNavigationDrawer
                        closeDrawer={closeDrawer}
                        styles={styles}
                        currentPageUrl={currentPageUrl}
                    />
                    <HeaderSignInNavigationDrawer
                        closeDrawer={closeDrawer}
                        styles={styles}
                        currentPageUrl={currentPageUrl}
                    />
                    {hasMiniCart && (
                        <PanelRow
                            className="cart-row"
                            {...styles.mainNavigationRow}
                            isCurrent={currentPageUrl === cartPageUrl}
                            onClick={closeDrawer}
                            onKeyPress={(event: React.KeyboardEvent) => {
                                if (event.key === " " && history && cartPageUrl) {
                                    event.preventDefault();
                                    closeDrawer();
                                    history.push(cartPageUrl);
                                }
                            }}
                            href={cartPageUrl}
                        >
                            <Typography className="cart-title" {...styles.logoLinks?.typographyProps}>
                                {translate("My Cart")}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
                <StyledSection className="drawer-section-wrapper separate mobile-only" {...styles.drawerSectionWrapper}>
                    <ZipCode {...props} />
                </StyledSection>
                {headerLinkListLinks.length > 0 && (
                    <StyledSection className="drawer-section-wrapper separate" {...styles.drawerSectionWrapper}>
                        {headerLinkListLinks.map((link, index) => (
                            <PanelRow
                                className="logo-links-row"
                                key={link.title + String(index)}
                                {...styles.mainNavigationRow}
                                isCurrent={currentPageUrl === link.url}
                                onClick={closeDrawer}
                                onKeyPress={(event: React.KeyboardEvent) => {
                                    if (event.key === " " && history && link?.url) {
                                        event.preventDefault();
                                        closeDrawer();
                                        history.push(link.url);
                                    }
                                }}
                                href={link.url}
                                target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                    {link.title}
                                </Typography>
                            </PanelRow>
                        ))}
                    </StyledSection>
                )}
                {isAuthenticated && (
                    <StyledSection className="drawer-section-wrapper separate tablet" {...styles.drawerSectionWrapper}>
                        <PanelRow
                            className="signOut-row"
                            {...styles.mainNavigationRow}
                            onClick={onSignOutHandler}
                            onKeyPress={(event: React.KeyboardEvent) => {
                                if (event.key === " ") {
                                    event.preventDefault();
                                    onSignOutHandler();
                                }
                            }}
                        >
                            <Typography className="signOut-title" {...styles.logoLinks?.typographyProps}>
                                {translate("Sign Out")}
                            </Typography>
                        </PanelRow>
                    </StyledSection>
                )}
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withDynamicGoogleMaps(withTheme(NavigationDrawer))));
